import { useNavigate } from "react-router-dom"
import { CheckCircle2, ArrowRight, ImageIcon } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Helmet } from "react-helmet-async"
import heroImage from "../../../assets/TopHeroImages/3.jpg"
const agronomieCtx = require.context("../../../assets/Agronomie", false, /\.(jpg|jpeg|png|webp)$/i)
const getImg = (s) => (typeof s === "string" ? s : s?.default ?? s)
const agronomiePhotos = agronomieCtx
    .keys()
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
    .map((k) => ({ src: getImg(agronomieCtx(k)), name: k.replace("./", "") }))
const iussPhotos = agronomiePhotos.slice(7)

const NAV = [
    { label: "Historique", id: "historique" },
    { label: "Organisation", id: "organisation" },
    { label: "Métier infirmier", id: "metier" },
    { label: "Soins infirmiers", id: "filiere-soins-infirmiers" },
    { label: "Santé bucco-dentaire", id: "filiere-sante-bucco-dentaire" },
    { label: "Évaluation", id: "evaluation" }
]

function PhotoGallery({ photos = [], label }) {
    return (
        <div className="mt-8">
            <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-accent" aria-hidden />
                {label}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Array.from({ length: 6 }, (_, i) => (
                    <div
                        key={i}
                        className="aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted/20 flex items-center justify-center"
                    >
                        {photos[i] ? (
                            <img
                                src={photos[i].src}
                                alt={`${label} - photo ${i + 1}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center gap-2 p-4 text-center border-2 border-dashed border-border w-full h-full">
                                <ImageIcon className="w-8 h-8 text-muted-foreground/50" aria-hidden />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Photo {i + 1}</span>
                                <span className="text-[9px] text-muted-foreground/80">A illustrer</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function Iuss() {
    const navigate = useNavigate()
    const soinsPhotos = iussPhotos.slice(0, 6)
    const buccoPhotos = iussPhotos.slice(6, 12)
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            <Helmet>
                <title>IUSS - Institut Universitaire des Sciences de la Santé | Université de Ngozi</title>
                <meta
                    name="description"
                    content="IUSS : Département des sciences de la santé. Filières Soins infirmiers et Santé bucco-dentaire. Baccalauréat professionnel, 3 ans, OM n° 530/264 du 7 mai 1999."
                />
            </Helmet>

            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {heroImage && (
                        <img
                            src={heroImage}
                            alt="IUSS - Institut Universitaire des Sciences de la Santé"
                            className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">IUSS</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Sciences de la Santé</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Institut Universitaire des Sciences</span>
                            <span className="block text-white mt-2">de la Santé</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                <strong className="font-semibold text-primary-foreground">Département des sciences de la santé</strong> — deux filières de niveau
                                supérieur : <span className="italic">Soins infirmiers</span> et <span className="italic">Santé bucco-dentaire</span>.
                            </p>
                            <p className="text-base md:text-lg font-light text-primary-foreground/85 mt-3">
                                <span className="italic tracking-tight">3 ans · Baccalauréat professionnel · Système BMD.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button
                                onClick={() => navigate("/admission")}
                                className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl"
                            >
                                Candidater maintenant
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "2", label: "Filières" },
                        { val: "3 ans", label: "Formation" },
                        { val: "BMD", label: "Système" },
                        { val: "Bac. pro.", label: "Diplôme" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">Sommaire</p>
                            {NAV.map((item, i) => (
                                <a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth"
                                >
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item.label}
                                </a>
                            ))}
                        </nav>
                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En bref</h4>
                                <dl className="space-y-4 text-[11px]">
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2 gap-4">
                                        <dt className="font-bold shrink-0">Filières</dt>
                                        <dd className="text-muted-foreground text-right">Soins infirmiers · Santé bucco-dentaire</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Durée</dt>
                                        <dd className="text-muted-foreground">3 ans</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Diplôme</dt>
                                        <dd className="text-muted-foreground">Bac. pro.</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <dt className="font-bold">Réf.</dt>
                                        <dd className="text-muted-foreground">OM 530/264</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </aside>

                    <main className="space-y-16">
                        <section id="historique">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Historique
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    L&apos;Institut Universitaire des Sciences de la Santé « IUSS », en sigle, est l&apos;un des projets de développement social et
                                    sanitaire de l&apos;Université de Ngozi, autorisé par l&apos;
                                    <strong className="text-foreground">Ordonnance ministérielle n° 530/264 du 7 mai 1999</strong>.
                                </p>
                                <p>
                                    L&apos;IUSS, retenu par le conseil d&apos;administration comme levier d&apos;intervention de l&apos;Université de Ngozi pour améliorer la
                                    santé des populations, propose des formations professionnelles pour celles et ceux qui souhaitent devenir des professionnels de
                                    santé qualifiés.
                                </p>
                            </div>
                        </section>

                        <section id="organisation">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Organisation de la formation
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    Selon les exigences du système{" "}
                                    <strong className="text-foreground">BMD (Baccalauréat, Master et Doctorat)</strong>, un crédit vaut 25 heures ou 30 heures.
                                </p>
                                <p>La formation proposée à l&apos;IUSS comporte :</p>
                                <ul className="space-y-2 list-disc list-inside">
                                    <li>
                                        Des enseignements de type tronc commun couvrant la première année en totalité ou tout un semestre selon les spécificités des
                                        programmes développés.
                                    </li>
                                    <li>Des enseignements théoriques propres à la spécialité.</li>
                                    <li>
                                        Des stages professionnels en milieu hospitalier et/ou aux centres de santé ciblés, à la fin desquels un rapport de stages et
                                        une épreuve clinique de mise en situation professionnelle sont obligatoires.
                                    </li>
                                    <li>Un rapport de fin d&apos;études produit sur une problématique de santé proposée par l&apos;étudiant ou l&apos;institut.</li>
                                </ul>
                                <p>Pour les stages cliniques, une semaine de stage équivaut à 1 crédit, soit un travail accompli en 40 heures au maximum.</p>
                            </div>
                        </section>

                        <section id="metier">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Métier infirmier (filière Soins infirmiers)
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    Le métier d&apos;infirmier moderne est d&apos;évaluer l&apos;état de santé d&apos;une personne et d&apos;analyser les situations de soins ; concevoir
                                    et définir des projets de soins personnalisés ; planifier des soins, les prodiguer et les évaluer ; mettre en œuvre des
                                    traitements.
                                </p>
                                <p>
                                    Les infirmiers dispensent des soins préventifs, curatifs ou palliatifs, visant à promouvoir, maintenir et restaurer la santé ; ils
                                    contribuent à l&apos;éducation pour la santé et à l&apos;accompagnement des personnes ou des groupes dans leur parcours de soins, en lien
                                    avec leur projet de vie.
                                </p>
                                <p>
                                    Ils interviennent au sein d&apos;une équipe pluri-professionnelle, en structures de soins et à domicile, de manière autonome et en
                                    collaboration (CII).
                                </p>
                                <p>
                                    Les domaines d&apos;apprentissage des candidats infirmiers à l&apos;IUSS recouvrent{" "}
                                    <strong className="text-foreground">dix compétences</strong> développées progressivement sur les trois années de formation.
                                </p>
                                <p>
                                    Les compétences visées rejoignent celles de la filière infirmière supérieure à l&apos;INSP (depuis 2004), déjà engagée dans le
                                    dispositif Baccalauréat–Maîtrise.
                                </p>
                            </div>
                        </section>

                        {/* ——— IUSS.md : Filière Soins infirmiers ——— */}
                        <section id="filiere-soins-infirmiers" className="scroll-mt-4">
                            <div className="rounded-2xl border border-border bg-card/30 p-6 md:p-10 shadow-sm">
                                <p className="text-[10px] uppercase tracking-[0.25em] font-black text-accent mb-2">Département des sciences de la santé</p>
                                <h2 className="heading-4 text-foreground mb-8 flex flex-wrap items-center gap-3">
                                    <div className="w-1 h-8 bg-accent rounded-full shrink-0" />
                                    Filière : Soins infirmiers
                                </h2>

                                <div className="space-y-10">
                                    <div>
                                        <h3 className="text-base font-bold text-foreground mb-3">Objectif général de la formation</h3>
                                        <p className="prose-sm text-muted-foreground font-medium leading-relaxed">
                                            L&apos;objectif visé par ce programme de formation en soins infirmiers de niveau supérieur est de contribuer au développement
                                            sanitaire de notre pays en mettant à la disposition des structures sanitaires des ressources humaines compétentes en santé,
                                            capables de répondre aux principaux enjeux des soins en milieu hospitalier et communautaire ainsi qu&apos;au bon fonctionnement
                                            des services infirmiers. Le contenu de la formation est ici centré sur l&apos;apprenant et le développement de sa personne en tant
                                            qu&apos;acteur de soins.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-base font-bold text-foreground mb-3">Prérequis pour intégrer la formation</h3>
                                        <ul className="space-y-3 text-muted-foreground font-medium text-sm leading-relaxed list-disc list-inside">
                                            <li>
                                                <strong className="text-foreground">Première année (B1)</strong> — les candidats concernés se présentent personnellement
                                                aux inscriptions ; ils doivent détenir le niveau des humanités techniques ou générales et avoir réussi à l&apos;examen
                                                d&apos;État.
                                            </li>
                                            <li>
                                                <strong className="text-foreground">Deuxième année (B2)</strong> — validation de tous les crédits et des stages prévus en
                                                B1 (conformément au règlement académique) et satisfaction des autres exigences pour valider ce niveau.
                                            </li>
                                            <li>
                                                <strong className="text-foreground">Troisième année (B3)</strong> — admission sous réserve de la validation de tous les
                                                crédits et des stages prévus en B2.
                                            </li>
                                        </ul>
                                        <p className="mt-4 text-sm text-muted-foreground font-medium leading-relaxed">
                                            L&apos;admission au <strong className="text-foreground">diplôme de bachelier en soins infirmiers</strong> est conditionnée par
                                            la réussite à l&apos;épreuve clinique et aux stages pratiques prévus, en lien avec le parcours suivi et la spécialité demandée.
                                            En outre, l&apos;étudiant ou l&apos;étudiante devra défendre un travail de fin de cycle.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-base font-bold text-foreground mb-3">Débouchés professionnels et secteurs d&apos;insertion</h3>
                                        <p className="text-sm text-muted-foreground font-medium mb-3">
                                            Les lauréats titulaires du{" "}
                                            <strong className="text-foreground">diplôme de baccalauréat professionnel</strong> délivré à l&apos;IUSS sont notamment
                                            orientés vers :
                                        </p>
                                        <ul className="space-y-2 list-disc list-inside text-muted-foreground font-medium text-sm leading-relaxed">
                                            <li>
                                                Le Ministère de la Santé publique et de la lutte contre le SIDA, qui pilote habituellement les ressources humaines en
                                                santé ;
                                            </li>
                                            <li>
                                                Les hôpitaux publics ou privés, pour l&apos;offre de soins et l&apos;encadrement pratique des stagiaires paramédicaux ;
                                            </li>
                                            <li>
                                                Les partenaires du Ministère de la santé publique et de la lutte contre le SIDA (ONG, organismes, etc.) ;
                                            </li>
                                            <li>Les institutions de formation infirmières.</li>
                                        </ul>
                                        <p className="mt-4 text-sm text-muted-foreground font-medium leading-relaxed">
                                            Dans la mesure du possible, les meilleurs lauréats pourront être recommandés pour suivre une formation de niveau{" "}
                                            <strong className="text-foreground">Mastère professionnel</strong> au deuxième cycle localement ou à l&apos;extérieur, par la voie de
                                            la compétition.
                                        </p>
                                        <div className="mt-6 flex flex-wrap gap-3">
                                            {["MSP & lutte contre le SIDA", "Hôpitaux publics et privés", "ONG et organismes", "Écoles infirmières"].map((item, i) => (
                                                <div key={i} className="flex items-center gap-2 p-3 bg-secondary/5 border border-border/50 rounded-lg">
                                                    <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" aria-hidden />
                                                    <span className="text-[11px] font-bold uppercase tracking-tight">{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <PhotoGallery photos={soinsPhotos} label="Photos de la formation" />

                                    <div className="pt-4 border-t border-border">
                                        <h3 className="text-base font-bold text-foreground mb-4">Responsable de la formation</h3>
                                        <div className="p-5 rounded-xl border border-border bg-primary/5 max-w-md">
                                            <p className="text-[10px] uppercase tracking-wider font-black text-muted-foreground mb-1">Soins infirmiers</p>
                                            <p className="text-foreground font-bold text-lg">Sr BEDETSE Espérance</p>
                                            <p className="text-sm text-muted-foreground mt-1">Chef de département</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ——— IUSS.md : Filière Santé bucco-dentaire ——— */}
                        <section id="filiere-sante-bucco-dentaire" className="scroll-mt-4">
                            <div className="rounded-2xl border border-border bg-card/30 p-6 md:p-10 shadow-sm">
                                <p className="text-[10px] uppercase tracking-[0.25em] font-black text-accent mb-2">Département des sciences de la santé</p>
                                <h2 className="heading-4 text-foreground mb-8 flex flex-wrap items-center gap-3">
                                    <div className="w-1 h-8 bg-accent rounded-full shrink-0" />
                                    Filière : Santé bucco-dentaire
                                </h2>

                                <div className="space-y-10">
                                    <div>
                                        <h3 className="text-base font-bold text-foreground mb-3">Objectif général de la formation</h3>
                                        <p className="prose-sm text-muted-foreground font-medium leading-relaxed">
                                            L&apos;objectif visé par ce programme de formation en santé bucco-dentaire de niveau supérieur est de contribuer au développement
                                            sanitaire de notre pays en mettant à la disposition des structures sanitaires des ressources humaines compétentes en santé,
                                            capables de répondre aux principaux enjeux des soins en milieu hospitalier et communautaire ainsi qu&apos;au fonctionnement des
                                            services d&apos;odontostomatologie. Le contenu de la formation est ici centré sur l&apos;apprenant et le développement de sa personne
                                            en tant qu&apos;acteur de soins.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-base font-bold text-foreground mb-3">Prérequis pour intégrer la formation</h3>
                                        <ul className="space-y-3 text-muted-foreground font-medium text-sm leading-relaxed list-disc list-inside">
                                            <li>
                                                <strong className="text-foreground">Premier baccalauréat (B1)</strong> — niveau des humanités techniques ou générales et
                                                réussite à l&apos;examen d&apos;État.
                                            </li>
                                            <li>
                                                <strong className="text-foreground">Deuxième baccalauréat (B2)</strong> — validation de tous les crédits obtenus en B1
                                                (conformément au règlement académique) et satisfaction des autres exigences pour valider le niveau.
                                            </li>
                                            <li>
                                                <strong className="text-foreground">Troisième baccalauréat (B3)</strong> — validation de tous les crédits obtenus en B2.
                                            </li>
                                        </ul>
                                        <p className="mt-4 text-sm text-muted-foreground font-medium leading-relaxed">
                                            L&apos;admission au <strong className="text-foreground">diplôme de bachelier en santé bucco-dentaire</strong> est conditionnée par
                                            la réussite à l&apos;épreuve clinique et aux stages pratiques prévus, en lien avec le parcours suivi et la spécialité demandée.
                                            En outre, l&apos;étudiant ou l&apos;étudiante devra présenter et défendre le travail de fin de cycle.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-base font-bold text-foreground mb-3">Débouchés professionnels et secteurs d&apos;insertion</h3>
                                        <p className="text-sm text-muted-foreground font-medium mb-3">
                                            Les lauréats titulaires du{" "}
                                            <strong className="text-foreground">diplôme de baccalauréat professionnel</strong> délivré à l&apos;IUSS sont notamment orientés
                                            vers :
                                        </p>
                                        <ul className="space-y-2 list-disc list-inside text-muted-foreground font-medium text-sm leading-relaxed">
                                            <li>
                                                Le Ministère de la Santé publique et de la lutte contre le SIDA, qui pilote habituellement les ressources humaines en
                                                santé ;
                                            </li>
                                            <li>
                                                Les hôpitaux publics ou privés, pour l&apos;offre de soins et l&apos;encadrement pratique des stagiaires paramédicaux ;
                                            </li>
                                            <li>
                                                Les partenaires du Ministère de la santé publique et de la lutte contre le SIDA (ONG, organismes, etc.) ;
                                            </li>
                                            <li>Les institutions de formation en santé bucco-dentaire.</li>
                                        </ul>
                                        <p className="mt-4 text-sm text-muted-foreground font-medium leading-relaxed">
                                            Dans la mesure du possible, les meilleurs lauréats pourront être recommandés pour suivre une formation de niveau{" "}
                                            <strong className="text-foreground">Mastère professionnel</strong> au deuxième cycle localement ou à l&apos;extérieur, par la voie de
                                            la compétition.
                                        </p>
                                    </div>

                                    <PhotoGallery photos={buccoPhotos} label="Photos de la formation" />

                                    <div className="pt-4 border-t border-border">
                                        <h3 className="text-base font-bold text-foreground mb-4">Responsable de la formation</h3>
                                        <div className="p-5 rounded-xl border border-border bg-primary/5 max-w-md">
                                            <p className="text-[10px] uppercase tracking-wider font-black text-muted-foreground mb-1">Santé bucco-dentaire</p>
                                            <p className="text-foreground font-bold text-lg">Dr BIGIRIMANA Julie</p>
                                            <p className="text-sm text-muted-foreground mt-1">Chef du département de Santé bucco-dentaire</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section id="evaluation">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Dispositif d&apos;évaluation
                            </h2>
                            <ul className="space-y-2 text-muted-foreground font-medium list-disc list-inside prose-sm">
                                <li>
                                    Une évaluation sous deux formes : deux sessions normales semestrielles et une session de rattrapage annuelle au titre de la deuxième
                                    session.
                                </li>
                                <li>Les coefficients correspondent aux crédits alloués à l&apos;activité ou au cours.</li>
                                <li>La nature des épreuves varie selon les matières enseignées ; plusieurs modalités peuvent être combinées :</li>
                            </ul>
                            <ul className="mt-4 space-y-1 text-muted-foreground text-sm list-disc list-inside pl-4">
                                <li>Examen pratique pour les cours à dominante technique ;</li>
                                <li>Examen écrit pour les cours théoriques dispensés en cours magistral ;</li>
                                <li>Travail individuel ou de groupe en contrôle continu des enseignements théoriques ;</li>
                                <li>Rapport de fin de cycle sous la forme d&apos;une problématique de santé ;</li>
                                <li>Mise en situation professionnelle en milieu réel en fin de cycle.</li>
                            </ul>
                        </section>
                    </main>
                </div>
            </article>

            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prochaine étape</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Soigner avec excellence.
                        <br />
                        Rejoignez l&apos;IUSS.
                    </h2>
                    <Button
                        onClick={() => navigate("/admission")}
                        className="bg-accent text-accent-foreground px-10 py-4 text-base font-black hover:bg-accent/90 transition-smooth group rounded-md shadow-2xl border-b-4 border-black/10"
                    >
                        Postuler maintenant
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </footer>
        </div>
    )
}
